import { ChangeEvent, Component, ComponentState, FocusEvent, KeyboardEvent, ReactNode } from 'react';
import type { Observable } from 'rxjs';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, mergeMap } from 'rxjs/operators';

import type {
  FilteringResultArray,
  ICommonTypeaheadProps,
  IInputPanelProps,
  ISelectingStatusSelecting,
  ITypeaheadOnfilter,
  SelectingStatus,
} from '../Typeahead.models';
import { TypeaheadPresentation } from '../Typeahead.presentation/Typeahead.presentation';

export interface ITypeaheadProps<TItem> extends ICommonTypeaheadProps<TItem>, ITypeaheadOnfilter<TItem> {
  isValidItem?: (item: TItem) => boolean;
  areItemsEqual?: (item1: Nullable<TItem>, item2: Nullable<TItem>) => boolean;
  convertItemToString?: (item: TItem) => string;
  children?: (props: ISelectingStatusSelecting<TItem>) => ReactNode;
}

interface ITypeaheadState<TItem> extends ComponentState {
  selectedItem: Nullable<TItem>;
  searchText: string;
  searchTextIncreased: boolean;
  filteredValues: FilteringResultArray<TItem>;
  indexToBeSelected: Nullable<number>;
  selecting: boolean;
  fetchingResults: boolean;
}

export class Typeahead<TItem> extends Component<ITypeaheadProps<TItem>, ITypeaheadState<TItem>> {
  public state: ITypeaheadState<TItem> = {
    selectedItem: this.props.selectedItem,
    searchText: this.convertItemToString(this.props.selectedItem),
    searchTextIncreased: true,
    filteredValues: [],
    indexToBeSelected: null,
    selecting: false,
    fetchingResults: false,
  };

  private valueToSuggestFor$ = new Subject<string>();
  private suggestionsSubscription: Subscription;

  constructor(props: ITypeaheadProps<TItem>) {
    super(props);
    this.changeSelectedItem = this.changeSelectedItem.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onInputKeyDown = this.onInputKeyDown.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.onInputFocus = this.onInputFocus.bind(this);

    this.suggestionsSubscription = Subscription.EMPTY;
  }

  public componentDidMount() {
    const suggestions$: Observable<FilteringResultArray<TItem>> = this.valueToSuggestFor$.pipe(
      debounceTime(this.props.debounce || 300, this.props.scheduler),
      mergeMap(searchText => this.props.onFilter(searchText)),
    );

    this.suggestionsSubscription = suggestions$.subscribe(this.suggestionsUpdated);
  }

  public componentDidUpdate(prevProps: ITypeaheadProps<TItem>, prevState: ITypeaheadState<TItem>) {
    if (this.props.lostInputBeforeEditing && this.props.isFocused && !prevProps.isFocused) {
      this.onInputChange(this.props.lostInputBeforeEditing);
    }
    if (!this.areItemsEqual(this.props.selectedItem, prevProps.selectedItem)) {
      // oxlint-disable-next-line react/no-did-update-set-state : legacy component remove this behavior on refactor
      this.setState({
        ...this.buildStateSelectionChanged(this.props.selectedItem)(prevState),
      });
    }
  }

  public componentWillUnmount() {
    this.suggestionsSubscription.unsubscribe();
  }

  public render() {
    // state
    const { selecting, filteredValues, searchText, indexToBeSelected } = this.state;
    const selectingProps: SelectingStatus<TItem> =
      selecting === true
        ? {
            selecting,
            children: this.props.children,
            availableValues: filteredValues,
            selectedIndex: indexToBeSelected,
            onSelectionMouseDown: this.changeSelectedItem,
            searchText: this.state.searchText,
          }
        : { selecting };

    const hasErrors: boolean =
      this.props.hasErrors !== null && this.props.hasErrors !== undefined
        ? this.props.hasErrors // errors injected from outside components
        : this.props.required && !this.props.isDisabled // errors computed with local information
          ? !this.isValidItem(this.state.selectedItem)
          : false;

    const inputProps: IInputPanelProps = {
      searchText,
      name: this.props.name,
      size: this.props.size,
      placeholder: this.props.placeholder || '',
      hasError: hasErrors,
      isDisabled: this.props.isDisabled,
      locked: this.props.locked,
      isLoading: this.props.isLoading,
      required: this.props.required,
      tabIndex: this.props.tabIndex || 0,
      onInputChange: e => this.onInputChange(e.target.value),
      onInputKeyDown: this.onInputKeyDown,
      onInputBlur: this.onInputBlur,
      onInputFocus: this.onInputFocus,
      isCellInactive: this.props.isCellInactive,
      isFocused: this.props.isFocused,
    };
    return (
      <TypeaheadPresentation
        {...inputProps}
        {...selectingProps}
        className={this.props.className}
        fetchingResults={this.state.fetchingResults}
        displaySpinner={this.props.displaySpinner}
        e2e={this.props.e2e}
        id={this.props.id}
        name={this.props.name}
        onInputBlur={this.onInputBlur}
      />
    );
  }

  private changeSelectedItem = (selectedItem: TItem | null) => {
    const prevSelectedItem = this.state.selectedItem;
    const newState = this.buildStateSelectionChanged(selectedItem);
    this.setState(newState);

    if (!prevSelectedItem || prevSelectedItem !== selectedItem) {
      this.props.onSelectedItemChanged(selectedItem);
    }
  };

  private suggestionsUpdated = (suggestions: FilteringResultArray<TItem>) => {
    this.setState(this.buildStateFilteredValuesUpdated(suggestions));

    if (this.isAutoSelection() && suggestions.length === 1) {
      const onlySuggestion = suggestions[0];
      const suggestionIsCurrentItem = this.areItemsEqual(this.state.selectedItem, onlySuggestion);
      if (!suggestionIsCurrentItem) {
        this.changeSelectedItem(onlySuggestion);
      }

      if (this.props.onEditionCompleted) {
        this.props.onEditionCompleted();
      }
    }
  };

  private isAutoSelection = () => {
    return this.props.autoSelection ?? true;
  };

  // event handlers
  private readonly onInputChange = (searchText: string) => {
    const minLength = this.props.minLength || 0;

    if (this.state.selectedItem !== null && !searchText) {
      this.setState(this.buildStateSelectionChanged(null));
      this.props.onSelectedItemChanged(null);
    } else {
      this.setState(this.buildStateSearchTextUpdated(searchText));

      if (searchText.length >= minLength) {
        this.setState({ fetchingResults: true });
        this.valueToSuggestFor$.next(searchText);
      }
    }
  };

  private readonly onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const { key, currentTarget } = event;
    switch (key) {
      case 'Enter':
        event.preventDefault();
        this.changeSelectedItem(
          this.state.indexToBeSelected !== null ? this.state.filteredValues[this.state.indexToBeSelected] : null,
        );
        if (this.props.onEditingKeyDown) {
          this.props.onEditingKeyDown(event);
        }
        break;
      case 'Escape':
        event.persist();
        const blur = currentTarget.blur.bind(currentTarget);
        const convertedValue = this.convertItemToString(this.state.selectedItem);
        this.setState(this.buildStateSearchTextUpdated(convertedValue), blur);
        if (this.props.onEditingKeyDown) {
          this.props.onEditingKeyDown(event);
        }
        break;
      case 'Tab':
        if (this.props.onEditingKeyDown) {
          this.props.onEditingKeyDown(event);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setState(this.buildStateSelectPrevious);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.setState(this.buildStateSelectNext);
        break;
    }
  };

  private onInputFocus(event: ChangeEvent<HTMLInputElement>) {
    const searchText = this.state.searchText;
    const minLength = this.props.minLength || 0;
    const isMinSearchLengthReached =
      (!searchText && minLength === 0) || (!!searchText && searchText.length >= minLength);

    if (this.props.loadSuggestionsOnFocus !== false && isMinSearchLengthReached) {
      this.valueToSuggestFor$.next(searchText);
    }
    if (this.props && this.props.onFocus && typeof this.props.onFocus === 'function') {
      this.props.onFocus(event);
    }
  }

  private onInputBlur(event: FocusEvent<HTMLInputElement>) {
    if (event.relatedTarget && event.relatedTarget.classList.contains('dropdown-menu')) {
      // The user is clicking within the dropdown, for instance in order to scroll the list of suggestions: the blur event should be ignored.
      // The match is done by class name: originally, I tried to walk through the parents to find whether `event.target` is among the parents or not; it appears, however, that it is never inside. Using a class name should be good enough.
      event.preventDefault();
      event.stopPropagation();
      event.target.focus();
      return;
    }

    if (this.state.selectedItem !== null && !this.state.searchText) {
      this.setState(this.buildStateSelectionChanged(null));
      this.props.onSelectedItemChanged(null);
    } else {
      const convertedValue = this.convertItemToString(this.state.selectedItem);
      this.setState(this.buildStateSearchTextUpdated(convertedValue));

      if (this.state.selectedItem) {
        this.setState(this.buildStateSelectingDisabled());
      }
    }
  }

  // state builders
  private buildStateSelectNext({
    indexToBeSelected,
    filteredValues,
  }: ITypeaheadState<TItem>): Pick<ITypeaheadState<TItem>, 'indexToBeSelected'> {
    const length = filteredValues.length;
    const newIndex = length === 0 ? null : indexToBeSelected === null ? 0 : Math.min(length - 1, indexToBeSelected + 1);
    return { indexToBeSelected: newIndex };
  }

  private buildStateSelectPrevious({
    indexToBeSelected,
    filteredValues,
  }: ITypeaheadState<TItem>): Pick<ITypeaheadState<TItem>, 'indexToBeSelected'> {
    const length = filteredValues.length;
    const newIndex = length === 0 ? null : indexToBeSelected === null ? length - 1 : Math.max(0, indexToBeSelected - 1);
    return { indexToBeSelected: newIndex };
  }

  private buildStateSelectingDisabled() {
    return (): Pick<ITypeaheadState<TItem>, 'selecting'> => ({
      selecting: false,
    });
  }

  private buildStateFilteredValuesUpdated(filteredValues: FilteringResultArray<TItem>) {
    return (): Pick<
      ITypeaheadState<TItem>,
      'filteredValues' | 'selecting' | 'indexToBeSelected' | 'fetchingResults'
    > => {
      return {
        filteredValues,
        selecting: true,
        indexToBeSelected: filteredValues?.length ? 0 : null,
        fetchingResults: false,
      };
    };
  }

  private buildStateSelectionChanged(selectedItem: TItem | null) {
    return (
      componentState: Readonly<ITypeaheadState<TItem>>,
    ): Pick<
      ITypeaheadState<TItem>,
      'selectedItem' | 'searchText' | 'filteredValues' | 'indexToBeSelected' | 'selecting' | 'fetchingResults'
    > => {
      const convertedValue = this.convertItemToString(selectedItem);

      return {
        selectedItem,
        ...this.buildStateSearchTextUpdated(convertedValue)(componentState),
      };
    };
  }

  private buildStateSearchTextUpdated(searchText: string | null) {
    return (
      componentState: Readonly<ITypeaheadState<TItem>>,
    ): Pick<
      ITypeaheadState<any>,
      'searchText' | 'searchTextIncreased' | 'filteredValues' | 'indexToBeSelected' | 'selecting' | 'fetchingResults'
    > => {
      const searchTextIncreased =
        !componentState.searchText || (!!searchText && searchText.length > componentState.searchText.length);

      return {
        searchText: searchText || '',
        searchTextIncreased,
        filteredValues: [],
        indexToBeSelected: null,
        selecting: false,
        fetchingResults: false,
      };
    };
  }

  // helper methods
  private areItemsEqual(item1: TItem | null, item2: TItem | null) {
    if (this.props.areItemsEqual) {
      return this.props.areItemsEqual(item1, item2);
    }

    const item1Value = this.convertItemToString(item1);
    const item2Value = this.convertItemToString(item2);

    return item1Value === item2Value;
  }

  private convertItemToString(item: TItem | null): string {
    if (!item) {
      return '';
    }
    return this.props.convertItemToString ? this.props.convertItemToString(item) : String(item);
  }

  private isValidItem(item: TItem | null) {
    return this.props.isValidItem && item ? this.props.isValidItem(item) : !!item;
  }
}
